// src/components/SecuritySimulationContainer.jsx - FIXED VERSION
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // Import useLocation
import { Shield, Award, CheckCircle, Clock, AlertTriangle, Globe, Mail, Server, Folder, AlertCircle, X, Play } from 'lucide-react';
import apiClient from '../api/apiClient';

// Import all available simulators
import PhishingEmailSimulator from './simulation/PhishingEmailSimulator';
import WebBrowserSecuritySimulator from './simulation/WebBrowserSecuritySimulator';
import FileSystemSecuritySimulator from './simulation/FileSystemSecuritySimulator';
import NetworkSecuritySimulator from './simulation/NetworkSecuritySimulator';
import OSSecuritySimulator from './simulation/OSSecuritySimulator';
import SimulatedOS from './simulation/SimulatedOS';
import PretextingSimulator from './simulation/PretextingSimulator';
import { getChallengeById, submitGameResults } from '../api/games.api';
import PasswordSecuritySimulator from './simulation/PasswordSecuritySimulator';
import MobileOSSimulator from './simulation/MobileOSSimulator';
import MobileSecuritySimulation from './simulation/MobileSecuritySimulation';
import RealisticOSSimulator from './simulation/RealisticOSSimulator';
import RealisticBrowserSimulator from './simulation/RealisticBrowserSimulator';
import QnASimulator from './simulation/QnASimulator';
import { default as WhatsAppSimulator } from './simulation/WhatsAppSimulator';
import BrowserExtensionMalwareSimulator from './simulation/BrowserExtensionMalwareSimulator';
import EnhancedMalwareSimulator from './simulation/EnhancedMalwareSimulator';
import PasswordStrengthSimulator from './simulation/PasswordStrengthSimulator';
import VideoQnASimulator from './simulation/VideoQnASimulator';
import WiFiSecuritySimulation from './simulation/WiFiSecuritySimulation';
import NetworkMonitor from './simulation/NetworkMonitor';
import MessageSimulator from './simulation/MessageSimulator';
import InstagramSimulator from './simulation/InstagramSimulator';
import BadgeCelebration from './common/BadgeCelebration';
import SQLInjectionSimulator from './simulation/SQLInjectionSimulator';
import DDoSDefenseSimulator from './simulation/DDoSDefenseSimulator';
import HtmlSimulations from './simulation/HtmlSimulations';
import OpenAEVSimulator from './simulation/OpenAEVSimulator';



/**
 * Enhanced SecuritySimulationContainer with fixed completion flow and DB saving
 */
const SecuritySimulationContainer = ({ apiService, gamificationEngine }) => {
  const { currentUser } = useAuth();
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // <-- Add useLocation hook to access current query params
  const queryParams = new URLSearchParams(location.search);
  const isDemo = queryParams.get('demo') === 'true';

  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [simulationCompleted, setSimulationCompleted] = useState(false);
  const [results, setResults] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [submittingResults, setSubmittingResults] = useState(false);
  const [autoProgression, setAutoProgression] = useState(true); // Enable auto-progression by default
  const [nextChallengeTimer, setNextChallengeTimer] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(15); // Default 15 seconds countdown



  useEffect(() => {
    loadChallenge();
    try {
      if (challengeId && !isDemo) {
        apiClient.post('/games/session/start', {
          game_type: 'challenge',
          challenge_id: challengeId,
          metadata: { source: 'ChallengeHub' }
        }).catch(() => { });
      }
    } catch (_) { }
    setStartTime(Date.now());

    // Cleanup timer on unmount
    return () => {
      if (nextChallengeTimer) {
        clearTimeout(nextChallengeTimer);
      }
    };
  }, [challengeId]);

  // Countdown timer effect
  useEffect(() => {
    if (challenge && !simulationCompleted) {
      // Get estimated time from challenge metadata or default to 15 seconds
      const estimatedTime = challenge.metadata?.estimated_time || challenge.estimated_time || 15;
      setTimeRemaining(estimatedTime);

      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            // Redirect to challenges list when timer reaches 0
            navigate(-1);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [challenge, simulationCompleted, navigate]);

  // Reset completion state when challengeId changes
  useEffect(() => {
    if (challengeId) {
      setSimulationCompleted(false);
      setResults(null);
      setCountdown(0);
      setTimeRemaining(15);
      if (nextChallengeTimer) {
        clearTimeout(nextChallengeTimer);
        setNextChallengeTimer(null);
      }
    }
  }, [challengeId]);

  const loadChallenge = async () => {
    try {
      setLoading(true);
      setError(null);

      // console.log(`🔄 Loading challenge: ${challengeId}`);

      // Try to load from API first
      let challengeData;
      try {
        const apiResponse = await getChallengeById(challengeId);
        // Handle both wrapped {success, data} and direct object responses
        if (apiResponse?.success && apiResponse?.data) {
          challengeData = apiResponse.data;
          // console.log('✅ Challenge loaded from API (wrapped response):', challengeData.title);
        } else if (apiResponse && apiResponse.id) { // Check for a valid challenge object
          challengeData = apiResponse;
          // console.log('✅ Challenge loaded from API:', challengeData.title);
        } else {
          throw new Error('Invalid or empty API response for challenge');
        }
      } catch (apiError) {
        // console.warn('⚠️ API failed, using fallback challenge:', apiError.message);
      }

      // **FIX**: Process the raw challenge data to extract scenarios for the simulator.
      // The simulator components expect scenarios in the `content` property.
      // The API might provide them in `steps` or other fields.
      let scenarios = [];
      if (challengeData.steps && Array.isArray(challengeData.steps) && challengeData.steps.length > 0) {
        // console.log('✅ Extracting scenarios from challenge steps...');
        scenarios = challengeData.steps
          .sort((a, b) => (a.order_index || 0) - (b.order_index || 0)) // Ensure correct order
          .map(step => step.content)
          .filter(Boolean); // Filter out any null/undefined content
      } else if (challengeData.content && Array.isArray(challengeData.content) && challengeData.content.length > 0) {
        // console.log('✅ Using scenarios directly from challenge content field...');
        scenarios = challengeData.content;
      }

      // Set the processed challenge to state, ensuring `content` is populated.
      const processedChallenge = { ...challengeData, content: scenarios };
      // console.log('📊 Processed challenge data:', {
      //   id: processedChallenge.id,
      //   title: processedChallenge.title,
      //   description: processedChallenge.description,
      //   category: processedChallenge.category,
      //   content: processedChallenge.content,
      //   scenarios: scenarios
      // });
      setChallenge(processedChallenge);
    } catch (error) {
      console.error('❌ Failed to load challenge:', error);
      setError(`Failed to load challenge: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };


  const determineSimulatorType = (challenge, challengeId) => {
    // Log all the data we have to make a decision
    // console.log('🕵️‍♂️ Determining simulator type for:', {
    //   challengeId: challengeId,
    //   title: challenge?.title,
    //   description: challenge?.description,
    //   simulatorType: challenge?.simulatorType,
    //   category: challenge?.category,
    //   metadata: challenge?.metadata
    // });

    // 1. Check for an explicit simulator type on the challenge object. This is the most reliable.
    if (challenge?.simulatorType) {
      // console.log(`✅ Found explicit simulatorType: '${challenge.simulatorType}'`);
      return challenge.simulatorType;
    }


    // 3. If no explicit type, try to infer from category or ID.
    const combinedIdentifier = `${challenge?.category?.toLowerCase() || ''} ${challenge?.type?.toLowerCase() || ''}`;
    // console.log(`ℹ️ No explicit type. Using combined identifier for inference: "${combinedIdentifier}"`);

    if (combinedIdentifier.includes('os_sim') && combinedIdentifier.includes('malware_ransomware')) {
      // console.log(`➡️ Matched 'enhanced-malware'`);
      return 'enhanced-malware';
    } else if (combinedIdentifier.includes('os_browser') && combinedIdentifier.includes('malware_ransomware')) {
      // console.log(`➡️ Matched 'browser-extension-malware'`);
      return 'browser-extension-malware';
    } else if (combinedIdentifier.includes('video-qna')) {
      // console.log(`➡️ Matched 'video-qna'`);
      return 'video-qna';
    } else if (combinedIdentifier.includes('email')) {
      // console.log(`➡️ Matched 'email-phishing'`);
      return 'email-phishing';
    } else if (combinedIdentifier.includes('browser-os')) {
      // console.log(`➡️ Matched 'web-browser'`);
      return 'web-browser';
    } else if (combinedIdentifier.includes('sql')) {
      // console.log(`➡️ Matched 'sql'`);
      return 'sql';
    } else if (combinedIdentifier.includes('ddos')) {
      // console.log(`➡️ Matched 'ddos'`);
      return 'ddos';
    } else if (combinedIdentifier.includes('html_simulation')) {
      // console.log(`➡️ Matched 'html_simulation'`);
      return 'html_simulation';
    } else if (combinedIdentifier.includes('openaev')) {
      // console.log(`➡️ Matched 'openaev'`);
      return 'openaev';
    } else if ((combinedIdentifier.includes('network_security'))) {
      // console.log(`➡️ Matched 'network-security-monitor'`);
      return 'network-security-monitor';
    } else if (combinedIdentifier.includes('pretexting')) {
      // console.log(`➡️ Matched 'pretexting'`);
      return 'pretexting';
    } else if ((combinedIdentifier.includes('phone-call'))) { // This will catch 'mobile_phone' and 'mobile_security'
      // console.log(`➡️ Matched 'mobile_phone'`);
      return 'mobile_phone';
    } else if ((combinedIdentifier.includes('mobile-software-download'))) { // This will catch 'mobile_phone' and 'mobile_security'
      // console.log(`➡️ Matched 'mobile_download'`);
      return 'mobile_download';
    } else if (combinedIdentifier.includes('os-security') || combinedIdentifier.includes('operating')) {
      // console.log(`➡️ Matched 'os-security'`);
      return 'os-security';
    } else if (combinedIdentifier.includes('file') || combinedIdentifier.includes('system') || combinedIdentifier.includes('files')) {
      // console.log(`➡️ Matched 'file-system'`);
      return 'file-system';
    } else if (combinedIdentifier.includes('qna')) {
      return 'qna';
    } else if (combinedIdentifier.includes('fort')) {
      // console.log("Combined Identifier:", combinedIdentifier);
      return 'password-security';
    } else if (combinedIdentifier.includes('whatsapp')) {
      // console.log(`➡️ Matched 'whatsapp'`);
      return 'whatsapp';
    } else if (combinedIdentifier.includes('WiFi')) {
      // console.log(`➡️ Matched 'password-security'`);
      return 'wifi';
    } else if (combinedIdentifier.includes('sms')) {
      // console.log(`➡️ Matched 'password-security'`);
      return 'sms';
    } else if (combinedIdentifier.includes('insta')) {
      // console.log(`➡️ Matched 'password-security'`);
      return 'insta';
    }

    // 4. If no match, log a warning and use a default.
    console.warn(`⚠️ Could not determine simulator type for challenge "${challenge?.title}". Defaulting to 'enhanced-malware'.`);
    return 'enhanced-malware'; // Fallback to enhanced-malware for malware_ransomware category
  };

  const handleSimulationComplete = async (summary) => {
    // console.log('🎯 Simulation completed with summary:', summary);

    const endTime = Date.now();
    const durationSeconds = Math.round((endTime - startTime) / 1000);

    // Normalize summary for simple Q&A / video-QnA challenges that only send isCorrect
    const normalizedSummary = { ...summary };
    if ((normalizedSummary.total === undefined || normalizedSummary.total === null) &&
      typeof normalizedSummary.isCorrect === 'boolean') {
      normalizedSummary.total = 1;
      normalizedSummary.correct = normalizedSummary.isCorrect ? 1 : 0;
      normalizedSummary.incorrect = normalizedSummary.isCorrect ? 0 : 1;
    } else {
      normalizedSummary.total = normalizedSummary.total || 0;
      normalizedSummary.correct = normalizedSummary.correct || 0;
      normalizedSummary.incorrect = normalizedSummary.incorrect || 0;
    }

    const score = normalizedSummary.total > 0
      ? Math.round((normalizedSummary.correct / normalizedSummary.total) * 100)
      : 0;

    // **FIXED**: Only calculate XP if user got correct answers
    let earnedXp = 0;
    if (score > 0) {
      const baseXp = challenge?.points || 50;
      const difficultyMultiplier = {
        'beginner': 1.0,
        'intermediate': 1.2,
        'advanced': 1.5
      }[challenge?.difficulty] || 1.0;

      earnedXp = Math.round(baseXp * (score / 100) * difficultyMultiplier);
    }

    const finalResults = {
      ...normalizedSummary,
      completionTime: durationSeconds,
      score,
      earnedXp
    };

    if (summary.newBadges) {
      finalResults.newBadges = summary.newBadges;
    }


    setResults(finalResults);
    setSimulationCompleted(true);

    // console.log('💾 Starting result submission process...');

    if (!isDemo) {
      // Submit results to backend (async, don't wait)
      submitResultsToBackend(finalResults, summary);

      // Award XP via gamification engine (local)
      if (gamificationEngine && gamificationEngine.recordActivityComplete) {
        try {
          gamificationEngine.recordActivityComplete(challengeId, {
            score,
            xpValue: earnedXp
          });
          // console.log('🎮 Gamification engine updated successfully');
        } catch (error) {
          console.warn('⚠️ Error recording activity completion in gamification engine:', error);
        }
      }
    }

    // Auto-progress to next challenge if enabled
    if (autoProgression && score >= 70) { // Only auto-progress if passing score
      // console.log('🔄 Auto-progression enabled, starting countdown...');
      startAutoProgressionCountdown();
    }
  };

  const startAutoProgressionCountdown = () => {
    setCountdown(10); // 10 second countdown

    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          loadNextChallenge();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Store interval for cleanup
    setNextChallengeTimer(countdownInterval);
  };

  const loadNextChallenge = async () => {
    // DEMO MODE: Always return to Demo Arena, never progress to real challenges
    if (isDemo) {
      // console.log('Demo mode completed - returning to Demo Arena');
      navigate('/demo-arena');
      return;
    }

    // Helper function to get arena ID from challenge or query params
    const getArenaId = () => {
      const queryParams = new URLSearchParams(location.search);
      const arenaFromQuery = queryParams.get('arena');

      // Try to get arena from challenge data
      const currentChallenge = challenge;
      const arenaFromChallenge =
        currentChallenge?.associated_arena ||
        currentChallenge?.metadata?.arena ||
        currentChallenge?.arena ||
        currentChallenge?.category; // Use category as fallback (e.g., 'phishing_social_engineering')

      return arenaFromQuery || arenaFromChallenge;
    };

    const arenaId = getArenaId();
    const queryParams = new URLSearchParams(location.search);
    const topicFilter = queryParams.get('topic');

    // Helper function to build challenges URL with arena
    const buildChallengesUrl = () => {
      if (arenaId) {
        return `/challenges?arena=${arenaId}${topicFilter ? `&topic=${encodeURIComponent(topicFilter)}` : ''}`;
      }
      return '/challenges';
    };

    try {
      // console.log('Loading next challenge with sequential selection...');

      if (!apiService) {
        console.warn('⚠️ apiService not available, returning to challenges');
        navigate(buildChallengesUrl());
        return;
      }

      const currentChallenge = challenge;
      const currentCategory = currentChallenge?.category || 'general';
      const currentArena = currentChallenge?.associated_arena ||
        currentChallenge?.metadata?.arena ||
        currentChallenge?.arena;

      // Build query similar to ChallengeHub so order matches arena view
      // Use perDifficulty to fetch 10 challenges per difficulty level
      const apiParams = {
        limit: '10',
        perDifficulty: 'true'
      };
      if (arenaId || currentArena || currentCategory) {
        apiParams.arena = arenaId || currentArena || currentCategory;
      }
      if (topicFilter) {
        apiParams.topics = topicFilter;
      }

      const response = await apiService.get(
        `/challenges?${new URLSearchParams(apiParams).toString()}`
      );

      if (response.success && Array.isArray(response.data) && response.data.length > 0) {
        // Sort by explicit order (or id) exactly like ChallengeHub
        const sorted = [...response.data].sort((a, b) => {
          return (a.order || a.id) - (b.order || b.id);
        });

        // Prefer staying within the same difficulty level
        const currentDifficulty =
          currentChallenge?.difficulty != null ? String(currentChallenge.difficulty) : null;

        const sameDifficultyList = currentDifficulty
          ? sorted.filter((c) => String(c.difficulty) === currentDifficulty)
          : sorted;

        const listToUse = sameDifficultyList.length > 0 ? sameDifficultyList : sorted;

        // Find current challenge index in that ordered list
        const currentIndex = listToUse.findIndex(
          (c) => String(c.id) === String(challengeId)
        );

        if (currentIndex !== -1) {
          const nextIndex = currentIndex + 1;

          if (nextIndex < listToUse.length) {
            const nextChallenge = listToUse[nextIndex];

            // Check for subscription if moving to Level 3 (Advance)
            const nextDiff = String(nextChallenge.difficulty);
            if (nextDiff === '3' && !currentUser?.current_plan_id) {
              alert(
                'Your current pack allows only challenge experience till The Gauntlet level. For Advancing to next level, please upgrade your subscription'
              );
              navigate(buildChallengesUrl());
              return;
            }

            // Preserve the arena and topic query parameters for the next challenge URL
            const arenaParams = location.search; // full "?arena=...&topic=..."

            // Reset state before navigation
            setSimulationCompleted(false);
            setResults(null);
            setCountdown(0);
            if (nextChallengeTimer) {
              clearTimeout(nextChallengeTimer);
              setNextChallengeTimer(null);
            }

            // Navigate to the next challenge in sequence
            navigate(`/challenge/${nextChallenge.id}${arenaParams}`, {
              state: { imageUrl: nextChallenge.imageUrl }
            });
            return;
          }

          // console.log('🏁 Reached end of challenge sequence for this difficulty/arena.');
          navigate(buildChallengesUrl());
          return;
        }
      }

      // console.log('ℹ️ No suitable next challenge found, returning to challenges');
      navigate(buildChallengesUrl());
    } catch (error) {
      console.error('❌ Error loading next challenge:', error);
      navigate(buildChallengesUrl());
    }
  };

  const cancelAutoProgression = () => {
    if (nextChallengeTimer) {
      clearTimeout(nextChallengeTimer);
      setNextChallengeTimer(null);
    }
    setCountdown(0);
    setAutoProgression(false);
  };

  const submitResultsToBackend = async (finalResults, summary) => {
    if (submittingResults) {
      // console.log('⚠️ Results already being submitted, skipping...');
      return;
    }

    setSubmittingResults(true);

    try {
      // Helper function to check if a string is a valid UUID
      const isValidUUID = (str) => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return str && typeof str === 'string' && uuidRegex.test(str);
      };

      // Clean decisions to remove any arena_id that's not a valid UUID
      const cleanedDecisions = { ...(summary.decisions || {}) };
      if (cleanedDecisions.arena_id && !isValidUUID(cleanedDecisions.arena_id)) {
        delete cleanedDecisions.arena_id;
      }

      const gameData = {
        challengeId: challenge.id,
        gameType: determineSimulatorType(challenge, challengeId),
        score: finalResults.score,
        timeSpent: finalResults.completionTime,
        decisions: cleanedDecisions,
        correct: finalResults.correct || summary.correct || 0,
        incorrect: finalResults.incorrect || summary.incorrect || 0,
        total: finalResults.total || summary.total || 0,
        earnedXp: finalResults.earnedXp,
        // DO NOT send arena_id - backend will use challenge.category as fallback
        // Only include arena_id if it's a valid UUID (not a category string like "malware_ransomware")
        // ...(challenge.arena_id && isValidUUID(challenge.arena_id) ? { arena_id: challenge.arena_id } : {}),
        completionData: {
          simulator_type: determineSimulatorType(challenge, challengeId),
          challenge_category: challenge.category,
          difficulty: challenge.difficulty,
          scenarios_completed: finalResults.total || 0,
          accuracy_rate: finalResults.score,
          completion_timestamp: new Date().toISOString(),
          using_fallback: challenge.usingFallback || false,
          auto_progression_enabled: autoProgression,
          total: finalResults.total || summary.total || 0 // Add total for XP calculation
          // DO NOT include arena_id here either
        }
      };

      // console.log('🎮 Submitting game results to backend:', gameData);

      const response = await submitGameResults(gameData);

      if (response && response.data && response.data.newBadges) {
        // console.log("🏆 New badges received from backend:", response.data.newBadges);
        setResults(prev => ({
          ...prev,
          newBadges: response.data.newBadges
        }));
      }

      // if (response.success) {
      //   console.log('✅ Game results saved to database:', response.data);
      // } else {
      //   console.warn('⚠️ Backend returned error for game results:', response.message);
      // }

    } catch (error) {
      console.error('❌ Failed to submit game results to backend:', error);
    } finally {
      setSubmittingResults(false);
    }
  };



  const renderSimulator = () => {
    if (!challenge) {
      return <div className="p-8 text-center text-red-400">Challenge data is missing.</div>;
    }

    const scenarios = challenge.content || [];
    const estimatedTime = challenge.metadata?.estimated_time || challenge.estimated_time || 15;
    const simulatorType = determineSimulatorType(challenge, challengeId);

    // console.log('🎮 SecuritySimulationContainer rendering simulator:', {
    //   simulatorType,
    //   scenarios,
    //   scenarioCount: scenarios.length,
    //   firstScenario: scenarios[0]
    // });

    switch (simulatorType) {
      case 'browser-extension-malware':
        return <BrowserExtensionMalwareSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'enhanced-malware':
        return <EnhancedMalwareSimulator challenge={challenge} onComplete={handleSimulationComplete} scenarios={scenarios} estimatedTime={estimatedTime} />;
      case 'web-browser':
        // For browser challenges, pass the first scenario that has browser-specific content
        const browserScenario = scenarios.find(s => s.pageType || s.initialUrl || s.targetUrl) || scenarios[0];
        return <RealisticBrowserSimulator scenario={browserScenario} scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'email-phishing':
      case 'email':
        return <PhishingEmailSimulator emails={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'network-security-monitor':
        return <NetworkSecuritySimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'pretexting':
      case 'mobile_phone':
        return <PretextingSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'mobile_download':
        return <MobileSecuritySimulation scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'file-system':
        return <RealisticOSSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'os-security':
        return <SimulatedOS osType="windows" initialActiveApp="security"><RealisticOSSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} /></SimulatedOS>;
      case 'password-security':
        return <PasswordStrengthSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'whatsapp':
        return <WhatsAppSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'qna':
        return <QnASimulator scenario={scenarios[0]} onComplete={handleSimulationComplete} challenge={challenge} />;
      case 'browser_security':
        return <RealisticBrowserSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'video-qna':
        return <VideoQnASimulator scenario={scenarios[0]} onComplete={handleSimulationComplete} challenge={challenge} />;
      case 'wifi':
        return <WiFiSecuritySimulation scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'sms':
        return <MobileOSSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'insta':
        return <InstagramSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'sql':
        return <SQLInjectionSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'ddos':
        return <DDoSDefenseSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'html_simulation':
        return <HtmlSimulations scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      case 'openaev':
        return <OpenAEVSimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
      default:
        return <QnASimulator scenarios={scenarios} onComplete={handleSimulationComplete} estimatedTime={estimatedTime} challenge={challenge} />;
    }
  };

  const renderCompletionScreen = () => (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-cover bg-center" style={{ backgroundImage: "url('/assets/images/achievement_bg.gif')" }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm z-0"></div>
      <div className="relative z-10 bg-gray-800/50 rounded-lg p-8 text-center max-w-2xl mx-auto border border-green-500/30 shadow-[0_0_5px_#ff0,0_0_15px_#ff0,0_0_25px_#ff0] hover:shadow-[0_0_10px_#0ff,0_0_20px_#0ff,0_0_30px_#0ff] transition-shadow">
        <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
        <h2 className="text-3xl font-bold mb-2 text-white">{isDemo ? 'Demo Completed!' : 'Challenge Complete!'}</h2>
        <p className="text-gray-400 mb-2">You've successfully completed the '{challenge?.title}' challenge.</p>

        {/* Show XP earned for correct answers */}
        {isDemo ? (
          <div className="bg-blue-900/30 border border-blue-500 rounded-lg p-4 mb-6">
            <p className="text-blue-300 font-medium">ℹ️ Demo Mode</p>
            <p className="text-sm text-blue-400">No points or progress are saved in demo mode.</p>
          </div>
        ) : results?.earnedXp > 0 ? (
          <div className="bg-green-900/30 border border-green-500 rounded-lg p-4 mb-6">
            <p className="text-green-300 font-medium">🎉 Earned {results.earnedXp} XP for correct answers!</p>
            <p className="text-sm text-green-400">Great job getting the answers right!</p>
          </div>
        ) : (
          <div className="bg-yellow-900/30 border border-yellow-500 rounded-lg p-4 mb-6">
            <p className="text-yellow-300 font-medium">⚠️ No XP earned this time</p>
            <p className="text-sm text-yellow-400">Try to get more answers correct to earn XP!</p>
          </div>
        )}

        {submittingResults && <p className="text-sm text-blue-400 mb-4">💾 Saving results to database...</p>}
        {challenge?.usingFallback && <p className="text-sm text-yellow-400 mb-4">⚠️ This challenge used fallback content due to server issues.</p>}
        {autoProgression && countdown > 0 && results?.score >= 70 && (
          <div className="bg-blue-900/30 border border-blue-500 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-300 font-medium">Next challenge starting in {countdown} seconds...</p>
                <p className="text-sm text-blue-400">Great job! Moving to the next challenge automatically.</p>
              </div>
              <button onClick={cancelAutoProgression} className="text-gray-400 hover:text-white" title="Cancel auto-progression"><X size={20} /></button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-white">
          <div className="bg-gray-700/50 p-4 rounded-lg backdrop-blur-sm">
            <div className="text-sm text-gray-400">Final Score</div>
            <div className="text-2xl font-bold">{results?.score || 0}%</div>
          </div>
          <div className="bg-gray-700/50 p-4 rounded-lg backdrop-blur-sm">
            <div className="text-sm text-gray-400">XP Earned</div>
            <div className="text-2xl font-bold text-purple-400">+{results?.earnedXp || 0} XP</div>
          </div>
          <div className="bg-gray-700/50 p-4 rounded-lg backdrop-blur-sm">
            <div className="text-sm text-gray-400">Time Taken</div>
            <div className="text-2xl font-bold">{results?.completionTime || 0}s</div>
          </div>
        </div>
        <div className="space-y-3">
          <button
            onClick={() => isDemo ? navigate('/demo-arena') : loadNextChallenge()}
            className="w-full px-6 py-3 bg-green-600 hover:bg-green-500 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors"
          >
            <span>{isDemo ? 'Back to Demo Arena' : 'Next Challenge'}</span>
          </button>
          <button
            onClick={() => navigate(isDemo ? '/demo-arena' : '/')}
            className="w-full px-6 py-3 bg-gray-600 hover:bg-red-500 rounded-lg font-medium transition-colors"
          >
            {isDemo ? 'Back to Demo Arena' : 'Save & Quit'}
          </button>
          {/* <button onClick={() => setAutoProgression(!autoProgression)} className={`w-full px-6 py-3 rounded-lg font-medium transition-colors ${autoProgression ? 'bg-green-600 hover:bg-green-500' : 'bg-gray-600 hover:bg-gray-500'}`}>Auto-progression: {autoProgression ? 'ON' : 'OFF'}</button> */}
        </div>
      </div>
      {results?.newBadges && results.newBadges.length > 0 && (
        <BadgeCelebration
          badges={results.newBadges}
          onClose={() => {
            // Optional: clear badges from state if you want, or just let them close
            // For now we just close the modal visually in the component
          }}
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading challenge...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Error Loading Challenge</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button onClick={() => navigate(-1)} className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium">Go Back</button>
        </div>
      </div>
    );
  }

  if (simulationCompleted) {
    return renderCompletionScreen();
  }

  return (
    <div key={challengeId} className="min-h-screen bg-gray-900 text-white">
      {!simulationCompleted && (
        <>
          {isDemo && (
            <div className="fixed top-1 left-1/2 -translate-x-1/2 z-50">
              <div className="bg-blue-600/90 text-white px-4 py-1 rounded-b-lg shadow-lg font-bold text-sm flex items-center">
                <Play size={14} className="mr-2 fill-current" /> DEMO MODE
              </div>
            </div>
          )}
          <div className="fixed top-1 right-2 sm:right-4 z-50">
            <div className={`flex items-center space-x-1 ${timeRemaining <= 5 ? 'bg-red-500/80' : 'bg-blue-500/80'} px-2 py-1 rounded-md border ${timeRemaining <= 5 ? 'border-red-300 shadow-md shadow-red-500/30' : 'border-blue-300 shadow-sm shadow-blue-500/20'} ${timeRemaining <= 5 ? 'animate-pulse' : ''}`}>
              <Clock size={14} className="text-white" />
              <span className="font-medium text-sm text-white">{timeRemaining}s</span>
              {timeRemaining <= 5 && <span className="hidden sm:inline text-white text-xs font-medium">Time running out!</span>}
            </div>
          </div>
        </>
      )}
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex items-start sm:items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 min-w-0">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors">←</button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold flex items-center truncate">{challenge?.title || 'Security Challenge'}</h1>
              <p className="text-gray-400 text-xs sm:text-sm line-clamp-2">{challenge?.description || 'Interactive cybersecurity training simulation'}</p>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto">{renderSimulator()}</div>
      </div>
    </div>
  );
};

export default SecuritySimulationContainer;